import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  HomeIcon,
  UsersIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import {
  ClipboardList,
  ListTodo,
  CheckSquare,
  UsersRound,
  DownloadIcon,
  MessageSquare,
} from "lucide-react";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMinimized: boolean;
  setIsMinimized: (val: boolean) => void;
}

export function Sidebar({
  isOpen,
  onClose,
  isMinimized,
  setIsMinimized,
}: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    if (isMinimized) return;
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    setExpandedSections([]);
  };

  const menuItems = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: HomeIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
    },
    {
      name: "Employees",
      href: "/employees",
      icon: UsersIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor"],
    },
    {
      name: "Attendance",
      href: "/attendance",
      icon: ClockIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
    },
    {
      name: "Leaves",
      href: "/leaves",
      icon: CalendarIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
    },
    {
      name: "Documents",
      href: "/documents",
      icon: DocumentTextIcon,
      roles: ["hr head", "manager", "supervisor", "employee"],
    },
    {
      name: "Task Management",
      icon: ClipboardList,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
      children: [
        { name: "Task Overview", href: "/tasks/dashboard" },
        { name: "All Tasks", href: "/tasks/list" },
        { name: "My Tasks", href: "/tasks/my-tasks" },
        { name: "Team Tasks", href: "/tasks/team-tasks" },
        { name: "Task Reports", href: "/tasks/reports" },
      ],
    },
    {
      name: "Work Monitor",
      icon: BriefcaseIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor"],
      children: [
        { name: "Work Schedule", href: "/screen-recording" },
        { name: "Activity Reports", href: "/activity-reports" },
        { name: "Activity Comparison", href: "/activity-comparison" },
      ],
    },
    {
      name: "Payroll",
      icon: CurrencyDollarIcon,
      roles: ["founder","director","admin", "hr head"],
      children: [
        { name: "Payroll Records", href: "/payroll-record" },
        { name: "Payroll Approval", href: "/approval-settings" },
        { name: "Payroll Settings", href: "/payroll-setting" },
      ],
    },
    {
      name: "Job Postings",
      href: "/jobs",
      icon: BriefcaseIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor"],
    },
    {
      name: "Reports",
      href: "/reports",
      icon: ChartBarIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor"],
    },
    {
      name: "Downloads",
      href: "/downloads",
      icon: DownloadIcon,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
    },
    {
      name: "Chat",
      href: "/chat",
      icon: MessageSquare,
      roles: ["founder","director","admin", "hr head", "manager", "supervisor", "employee"],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.role || "admin")
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "bg-white border-r border-gray-200 transition-all duration-300 flex flex-col h-full",
          isMinimized ? "w-20" : "w-64",
          isOpen
            ? "fixed top-0 left-0 z-50 translate-x-0"
            : "fixed top-0 left-0 z-50 -translate-x-full md:translate-x-0"
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "p-4 border-b flex items-center",
            isMinimized ? "justify-center" : "justify-between"
          )}
        >
          {!isMinimized && <span className="font-bold text-lg">Menu</span>}

          <button
            onClick={toggleMinimize}
            className="hidden md:block p-1 hover:bg-gray-100 rounded"
          >
            {isMinimized ? (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            )}
          </button>

          <button className="md:hidden ml-auto" onClick={onClose}>
            ✖
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const hasChildren = !!item.children;

            const isActive = item.children
              ? item.children.some((c) =>
                  location.pathname.startsWith(c.href)
                )
              : location.pathname.startsWith(item.href || "");

            const isExpanded =
              expandedSections.includes(item.name) || isActive;

            return (
              <div key={item.name} className="relative group">
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => toggleSection(item.name)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm",
                        isActive
                          ? "bg-blue-50 text-blue-700"
                          : "hover:bg-gray-100",
                        isMinimized && "justify-center"
                      )}
                      title={isMinimized ? item.name : ""}
                    >
                      <Icon className="h-5 w-5" />
                      {!isMinimized && (
                        <>
                          <span className="flex-1 text-left">
                            {item.name}
                          </span>
                          {isExpanded ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4" />
                          )}
                        </>
                      )}
                    </button>

                    {!isMinimized && isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={onClose}
                            className={cn(
                              "block px-3 py-2 rounded-lg text-sm",
                              location.pathname === child.href
                                ? "bg-blue-100 text-blue-700"
                                : "hover:bg-gray-100"
                            )}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href!}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm",
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "hover:bg-gray-100",
                      isMinimized && "justify-center"
                    )}
                    title={isMinimized ? item.name : ""}
                  >
                    <Icon className="h-5 w-5" />
                    {!isMinimized && <span>{item.name}</span>}
                  </Link>
                )}

                {/* Tooltip */}
                {isMinimized && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
                    {item.name}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
}